import type { ModelPricingRecord } from '@nadoutong/contracts';
import { useChannelModelsList } from '../api/useChannelModels.ts';
import { useChannelsList } from '../api/useChannels.ts';
import { useT } from '../i18n/index.ts';

interface Props {
  readonly vendor: string;
  readonly model: string;
  readonly currentPricing: ModelPricingRecord | null;
}

export function ChannelPricingCard({ vendor, model, currentPricing }: Props): JSX.Element {
  const t = useT();
  const { data: allModels, isLoading: loadingModels } = useChannelModelsList();
  const { data: channels, isLoading: loadingChannels } = useChannelsList();

  if (loadingModels || loadingChannels) {
    return (
      <div className="rounded-xl bg-surface-container p-6">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.08em] text-on-surface-variant">
          {t('model_detail.channel_pricing.title')}
        </h3>
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-8 animate-pulse rounded bg-surface-container-low" />
          ))}
        </div>
      </div>
    );
  }

  const entries = (allModels ?? []).filter(
    (cm) => cm.model === model,
  );
  const channelMap = new Map((channels ?? []).map((ch) => [ch.id, ch]));

  if (entries.length === 0) {
    return (
      <div className="rounded-xl bg-surface-container p-6">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.08em] text-on-surface-variant">
          {t('model_detail.channel_pricing.title')}
        </h3>
        <p className="text-sm text-outline">{t('model_detail.channel_pricing.empty')}</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-surface-container p-6" data-testid="channel-pricing-card">
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.08em] text-on-surface-variant">
        {t('model_detail.channel_pricing.title')}
      </h3>
      <table className="w-full text-left">
        <thead>
          <tr className="bg-surface-container-high/50">
            <th scope="col" className="px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-outline">{t('model_detail.channel_pricing.col_channel')}</th>
            <th scope="col" className="px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-outline">{t('model_detail.channel_pricing.col_multiplier')}</th>
            <th scope="col" className="px-4 py-2 text-right text-[10px] font-semibold uppercase tracking-[0.1em] text-outline">{t('model_detail.channel_pricing.col_input_price')}</th>
            <th scope="col" className="px-4 py-2 text-right text-[10px] font-semibold uppercase tracking-[0.1em] text-outline">{t('model_detail.channel_pricing.col_output_price')}</th>
            <th scope="col" className="px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-outline">{t('model_detail.channel_pricing.col_supplier')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-outline-variant/10">
          {entries.map((cm) => {
            const ch = channelMap.get(cm.channel_id);
            const rate = ch?.rate_multiplier ?? 1.0;
            const inputPrice = currentPricing !== null
              ? (currentPricing.input_per_mtok_usd * rate).toFixed(2)
              : '—';
            const outputPrice = currentPricing !== null
              ? (currentPricing.output_per_mtok_usd * rate).toFixed(2)
              : '—';
            return (
              <tr key={cm.channel_id} className="hover:bg-surface-bright/30 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-on-surface">{ch?.display_name ?? cm.channel_id}</span>
                    {ch?.is_official ? (
                      <span aria-hidden="true" className="material-symbols-outlined text-primary text-[14px]" style={{ fontVariationSettings: "'FILL' 1" }}>
                        verified
                      </span>
                    ) : null}
                  </div>
                  <span className="text-[11px] font-mono text-outline">{ch?.slug ?? '—'}</span>
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-primary">×{rate.toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-sm font-mono text-on-surface">${inputPrice}</td>
                <td className="px-4 py-3 text-right text-sm font-mono text-on-surface">${outputPrice}</td>
                <td className="px-4 py-3 text-sm text-outline">{ch?.supplier_name ?? '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
